import { Component, inject, OnInit } from '@angular/core';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import {
  IEconsentAcknowledgment,
  ILegalConsent,
  IPrimaryAcknowledgment
} from '@workflow/ecp-workflow/dynamic-app/types/legal-consent.type';
import { genTinyGUID } from '@cudirect/shared-ui-library';
import * as FullStory from '@fullstory/browser';
import { FieldInstance, FieldService } from '@workflow/class/field.service';
import { FlowService } from '@workflow/class/flow.service';
import { AnalyticsMetadataService } from '@app/service/analytics-metadata.service';

/** Shares & Services Picklist Path */
const PRIMARY_APPLICANT_TCPA_FIELD_ID = 'ss:instatouch-tcpa-consent-id';

@BuildingBlock('econsent-splash-screen')
@Component({
  selector: 'econsent-splash-screen',
  templateUrl: './econsent-splash-screen.component.html'
})
export class EConsentSplashComponent extends BuildingBlockControlBaseComponent implements OnInit {
  /** Variable to hold Consent Library */
  public consentLibrary: ILegalConsent[] = [];
  /** Variable to hold eConsent Lists */
  public eConsents: IEconsentAcknowledgment[] = [];
  /** Variable to hold Primary eConsent Data */
  public primaryEconsent: IPrimaryAcknowledgment;
  /** To Hold Primary Applicant's TCPA Field References */
  public tcpaConsentField: FieldInstance;

  /** Is page submitted */
  public isSubmitClicked: boolean;
  private eventPairId = genTinyGUID(24);

  private consentRootPath: string;
  private additionalConsentRootPath: string;
  private readonly fieldService = inject(FieldService);
  private readonly flowService = inject(FlowService);
  private readonly analyticsMetadataService = inject(AnalyticsMetadataService);
  /**
   * Initializes the component and checks the current application journey,
   * or creates an application journey if none exists.
   */
  public ngOnInit(): void {
    const currentFlow = this.flowService.createFlow(this.page.flowId);
    currentFlow.closeLoader();
    if (this.page.valid) {
      this.dynamicPageService.navigateNextPage();
      return;
    }

    this.dynamicPageService.pageSubmitted$.forEach((s) => {
      this.isSubmitClicked = s;
    });
    this.consentLibrary = this.adminPortalFeaturesService.config.consentManagement.consentLibrary;
    this.primaryEconsent = this.adminPortalFeaturesService.config.consentManagement.primaryAcknowledgement;
    const additonalConsents = this.adminPortalFeaturesService.config.consentManagement.additionalAcknowledgements;
    this.consentRootPath = this.field.payload.consentRootPath as string;
    this.additionalConsentRootPath = this.field.payload.additionalConsentRootPath as string;
    if (additonalConsents) {
      this.eConsents.push(...additonalConsents);
    }
    this.primaryEconsent.consentRootPath = this.field.payload.consentRootPath as string;
    this.eConsents.forEach((item, index) => {
      item.consentRootPath = this.additionalConsentRootPath + '/' + index;
      // const pageIterationId =
      //   item.consentRootPath.includes('#') && this.field?.pageIterationId === undefined ? 0 : this.field?.pageIterationId;
      const key = (item.consentRootPath + '/isAccepted')
        .replace('$', this.field.iterationId?.toString())
        .replace('#', this.field.pageIterationId?.toString());
      /**
       * if you want to know why I am using this to seed the checkboxes instead of
       * stateful value - it is because stateful value seems to hold on to the previous array
       * longer than it should, resulting in getting a value from a previous application, even with a timeout
       * it results in getting true, when the actual value was null (verified via network calls and debugging)
       * by using the StateManager, we are only working against the entire object, so what we see more closely matches
       * the network calls. StatefulValue and race conditions... Why did I expect anything else?
       */
      item.isAccepted = this.stateManager.getValue<boolean>(key) ?? false;
      // eslint-disable-next-line max-len
      // item.isAccepted = Boolean(this.statefulService.wrap(item.consentRootPath + '/isAccepted', this.field.iterationId, pageIterationId).value);
    });
    // Set Primary Applicant's TCPA Consent Field
    const fieldId = this.statefulService.wrap(PRIMARY_APPLICANT_TCPA_FIELD_ID).valueString;

    if (fieldId) {
      const pageIterationId = fieldId.includes('#') && this.field?.pageIterationId === undefined ? 0 : this.field?.pageIterationId;
      this.tcpaConsentField = this.fieldService.createField(fieldId, this.field?.flowId, null, pageIterationId);
    }
  }

  /** Navigate to the next page on API failure, Empty PID report & irrespective of the mid-app decision flag */
  public navigatetoNextPage(): void {
    this.dynamicPageService.pageSubmitted = true;

    if (this.page.valid) {
      const data = this.analyticsMetadataService.getMetadata(this.page);
      this.googleTag.publishEvent(data[0], data[1]);
      this.dynamicPageService.navigateNextPage();
    }
  }

  /** Method to capture econsent consent to bundle */
  public captureContent(consent: IEconsentAcknowledgment) {
    const pageIterationId =
      consent.consentRootPath.includes('#') && this.field?.pageIterationId === undefined ? 0 : this.field?.pageIterationId;
    const snapshot = this.statefulService.wrap(consent.consentRootPath + '/acceptanceSnapshotReferenceId', null, pageIterationId);
    let snapShotId: string = snapshot.value as string;
    if (!snapShotId) {
      snapShotId = crypto.randomUUID();
      snapshot.value = snapShotId;
    }
    const snapshotSave = () => {
      this.applicationApiService.captureConsent(this.field.data.protocol, {
        acknowledgementId: consent.acknowledgeId,
        acceptenceId: snapShotId,
        isCoApplicant: false // primary applicant only for splash screen
      });
      snapshot.unsubscribe(snapshotSave);
    };
    snapshot.afterPatch(snapshotSave);
  }
}
